import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProjectService } from '../../../project-intake/services/project.service';
import { UserLogin } from '../../../Common/models/user.login';
import { LocationDto } from '../../../project-intake/models/salesplan-vm';
import { MessageService } from '../../../project-intake/services/message.service';

@Component({
  selector: 'app-master-data-setup',
  templateUrl: './master-data-setup.component.html',
  styleUrls: ['./master-data-setup.component.css', './../../../../assets/css/master_management.css']
})
export class MasterDataSetupComponent {
  masterDataForm!: FormGroup;
  searchForm!: FormGroup;

  itemNames: any[] = ['Activity Type', 'Location', 'Display Type', 'Product Flow And Replenishment'];
  buttonText = 'SAVE';
  selectedItemToEdit: any = null;

  activities: any[] = [];
  locations: any = [];
  displayTypes: any = [];
  productFLowsList: any = [];

  lstMasterDataValues: any[] = [];
  filteredMasterDataValues: any[] = [];

  selectedName: string = '';
  isActiveFlagSelected: boolean = false;

  uInfo: UserLogin | null | undefined;

  constructor(private fb: FormBuilder, private projectService: ProjectService, private msgService: MessageService) { }

  ngOnInit(): void {
    let encodedVal = localStorage.getItem('UserRoleInfo');
    if (encodedVal != null) {
      this.uInfo = JSON.parse(atob(encodedVal));
    }

    this.masterDataForm = this.fb.group({
      name: [this.itemNames[0], Validators.required],
      value: ['', Validators.required],
      isActive: [false]
    });

    this.searchForm = this.fb.group({
      searchControl: ['']
    });

    this.selectedName = this.itemNames[0];

    this.loadValuesBasedOnName();

    this.searchForm.get('searchControl')?.valueChanges.subscribe(searchTerm => {
      this.filterValues(searchTerm);
    });
  }

  filterValues(searchTerm: string) {
    if (!searchTerm) {
      this.filteredMasterDataValues = [...this.lstMasterDataValues];
    } else {
      const lowerTerm = searchTerm.toLowerCase();
      this.filteredMasterDataValues = this.lstMasterDataValues.filter(item =>
        item.value.toLowerCase().includes(lowerTerm)
      );
    }
  }

  onNameChange() {
    this.selectedName = this.masterDataForm.get('name')?.value;
    this.loadValuesBasedOnName();
  }

  loadValuesBasedOnName() {
    switch (this.selectedName) {
      case 'Activity Type':
        this.getAllActivities();
        break;

      case 'Location':
        this.getLocations();
        break;

      case 'Display Type':
        this.getDisplayTypes();
        break;

      case 'Product Flow And Replenishment':
        this.getProductFlows();
        break;

      default:
        this.lstMasterDataValues = [];
        break;
    }
  }

  private getAllActivities() {
    this.projectService.getAllActivities().subscribe(data => {
      let lstActivitity = data.map((x: any) => ({ 'id': x.activityID, 'value': x.name, 'isActive': false }));
      this.lstMasterDataValues = lstActivitity;
      this.filteredMasterDataValues = [...this.lstMasterDataValues];
    });
  }

  private getLocations() {
    this.projectService.getLocations().subscribe(data => {
      let locations = data.map((x: any) => ({ 'id': x.locationId, 'value': x.locationName, 'isActive': x.isActive }));
      this.lstMasterDataValues = locations;
      this.filteredMasterDataValues = [...this.lstMasterDataValues];
    });
  }

  private getDisplayTypes() {
    this.projectService.getDisplayType().subscribe(data => {
      this.displayTypes = data;
      let locations = data.map((x: any) => ({ 'id': x.displayID, 'value': x.displayName, 'isActive': false }));
      this.lstMasterDataValues = locations;
      this.filteredMasterDataValues = [...this.lstMasterDataValues];
    });
  }

  private getProductFlows() {
    this.projectService.getPRoductFlows().subscribe(data => {
      let prodFLowList = data.map((x: any) => ({ 'id': x.productFlowID, 'value': x.productFlowDescription, 'isActive': x.isActive }));
      this.lstMasterDataValues = prodFLowList;
      this.filteredMasterDataValues = [...this.lstMasterDataValues];
    });
  }

  enableOrDisableItem(event: any, item: any): void {
    item.isActive = event.target.checked;
    this.selectedItemToEdit = item;
    this.saveValuesBasedOnName(item);
    this.clearForm();
    this.filterValues(this.searchForm.get('searchControl')?.value || '');
  }

  onEdit(item: any): void {
    this.selectedItemToEdit = item;

    this.masterDataForm.patchValue({
      name: this.selectedName,
      value: item.value,
      isActive: item.isActive
    });

    this.buttonText = 'UPDATE';
  }

  onSubmit(): void {
    if (this.masterDataForm.invalid) return;

    const formValue = this.masterDataForm.value;

    if (this.selectedItemToEdit) {
      // Update in memory
      this.selectedItemToEdit.value = formValue.value;
      this.selectedItemToEdit.name = formValue.name;
      this.selectedItemToEdit.isActive = formValue.isActive;
    } else {
      // Add to memory list
      const newItem = {
        name: formValue.name,
        value: formValue.value,
        isActive: true
      };
      this.lstMasterDataValues.push(newItem);
      this.selectedItemToEdit = newItem; // For save
    }

    this.saveValuesBasedOnName(this.selectedItemToEdit);
    this.clearForm();
    this.filterValues(this.searchForm.get('searchControl')?.value || '');
  }

  clearForm(): void {
    this.masterDataForm.patchValue({
      name: this.selectedName,
      value: '',
      isActive: true
    });

    this.selectedItemToEdit = null;
    this.buttonText = 'SAVE';
  }

  /** Routes item to correct backend save */
  saveValuesBasedOnName(item: any): void {
    switch (this.selectedName) {
      case 'Location':
        this.saveLocation(item);
        break;

      case 'Activity Type':
        // this.saveActivity(item);
        break;

      case 'Display Type':
        // this.saveDisplayType(item);
        break;

      case 'Product Flow And Replenishment':
        // this.saveProductFlow(item);
        break;

      default:
        break;
    }
  }

  private saveLocation(item: any): void {
    const locationDto = new LocationDto();
    locationDto.locationID = item?.id ?? 0;
    locationDto.locationName = item?.value ?? '';
    locationDto.isActive = item?.isActive ?? true;
    locationDto.createdBy = this.uInfo?.userID?.toString() ?? '';

    this.projectService.saveLocation(locationDto).subscribe({
      next: () => this.msgService.htmlSuccess("Location saved successfully"),
      error: err => this.msgService.htmlError("Error saving location: " + err.message)
    });
  }


}
